import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isNullOrUndefined } from "@/utilities";
import { setAuthUiState } from "@/slices/auth/authUi";
import { useAppDispatch } from "@/hooks/redux";
import { useLazyGetUserByIdQuery } from "@/slices/users/usersApi";
// Single shared Supabase client. This module used to call createClient() again,
// producing a second GoTrueClient on the same storage key — two independent
// refresh timers contending on the same navigator.locks name, which stalls
// every getSession() call the API layer makes.
import { supabase } from "@/utilities/supabase";

// Utility function to check if route is exempt from auth
const isRouteExemptFromAuth = (
  pathname: string,
  searchParams: URLSearchParams
) => {
  if (pathname?.toLowerCase()?.includes("documents")) {
    const token = searchParams.get("token");
    return token; // Return true if present
  }
  return false;
};

export const Auth = ({ children }: any) => {
  const [sessionState, setSessionState]: any = useState(null);
  const dispatch = useAppDispatch();
  const [triggerGetUserById] = useLazyGetUserByIdQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const isExempt = isRouteExemptFromAuth(location.pathname, searchParams);

  // The effect below runs once, so it must not close over changing values.
  // Keep the latest in a ref instead.
  const latest = useRef({ isExempt, navigate, dispatch, triggerGetUserById });
  latest.current = { isExempt, navigate, dispatch, triggerGetUserById };

  useEffect(() => {
    const handleSessionChange = async (session: any) => {
      setSessionState(session);

      if (!session || isNullOrUndefined(session)) {
        if (!latest.current.isExempt) {
          latest.current.navigate("/login");
        }
      } else {
        // `true` = preferCacheValue. This was `false`, which forced a network
        // refetch of the user on every single navigation.
        const { data: userRes } = await latest.current.triggerGetUserById(
          session?.user?.id,
          true
        );
        latest.current.dispatch(setAuthUiState(userRes));
      }
    };

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => handleSessionChange(session));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSessionChange(session);
    });

    return () => subscription.unsubscribe();
    // Mount-only. This previously depended on `location`, which is a new object
    // on every navigation — so each route change re-ran getSession(), tore down
    // and re-registered the auth subscription, forced a user refetch, and
    // re-dispatched authUi (re-rendering every subscriber).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return sessionState || isExempt ? children : null;
};
